import React, { useMemo, useRef, useCallback } from "react";
import { Group } from "@visx/group";
import { scaleLinear, scalePoint } from "@visx/scale";
import { GridRows } from "@visx/grid";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { TooltipWithBounds, defaultStyles, useTooltip } from "@visx/tooltip";
import { localPoint } from "@visx/event";
import { Flex, Text } from "@chakra-ui/react";
import {
  primary,
  secondary,
  backgroundGrey,
  textSecondary,
} from "../../../../utils/colors";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import { path as d3Path } from "d3-path";
import { motion } from "framer-motion";
import useThemeColors from "../../../../hooks/useThemeColors";
import useNumberFormatter from "../../../../hooks/useNumberFormatter";
import { useTranslation } from "react-i18next";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";

dayjs.extend(localeData);

const defaultMargin = { top: 20, right: 3, bottom: 20, left: 39 };

const ServicesLineChart = ({ width, height, data, currency }) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const formatNumber = useNumberFormatter();

  // COLORS
  const { backgroundGreyMode } = useThemeColors();

  const currencySymbol = useCurrencySymbol(currency);

  // accessors with guard clauses
  const getMonth = (d) => (d ? d.month : "");
  const getTotalPrice = (d) => (d ? d.totalPrice : 0);
  const getCompletedPrice = (d) => (d ? d.completedServicesTotalPrice : 0);

  // Helper function to generate path string
  const getLinePath = (data, xScale, yScale, xAccessor, yAccessor) => {
    const path = d3Path();
    path.moveTo(xScale(xAccessor(data[0])), yScale(yAccessor(data[0])));
    data.forEach((d) => {
      path.lineTo(xScale(xAccessor(d)), yScale(yAccessor(d)));
    });
    return path.toString();
  };

  // TOOLTIP
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipTop,
    tooltipLeft,
    tooltipOpen,
  } = useTooltip({
    tooltipOpen: false,
  });

  // bounds
  const xMax = width - defaultMargin.right - defaultMargin.left;
  const yMax = height - defaultMargin.top - defaultMargin.bottom;

  // Format the data to show only the required 6 months
  const monthsToShow = data.slice(0, 6);

  const getMonthLabel = (monthNum) => {
    return dayjs()
      .month(monthNum - 1)
      .format("MMM");
  };

  const getMonthLabelFull = (monthNum) => {
    return dayjs()
      .month(monthNum - 1)
      .format("MMMM");
  };

  const monthsToShowFormatted = monthsToShow.map((item) => ({
    ...item,
    month: getMonthLabel(item.month),
    monthNumber: item.month,
    year: item.year, // assuming each item has a `year` field
  }));

  const getMonthLabelFullWithYear = (monthNum, year) => {
    return dayjs()
      .year(year)
      .month(monthNum - 1)
      .format("MMMM YYYY");
  };

  // Use scalePoint for better line extension across the axis
  const xScale = useMemo(
    () =>
      scalePoint({
        range: [0, xMax],
        domain: monthsToShowFormatted.map(getMonth), // X axis with months
        padding: 0.2, // This adds space between points, you can adjust
      }),
    [xMax, monthsToShowFormatted]
  );

  const yScale = useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0],
        round: true,
        nice: false,
        domain: [
          0,
          Math.max(
            ...monthsToShowFormatted.map((d) =>
              Math.max(getTotalPrice(d), getCompletedPrice(d))
            )
          ),
        ],
      }),
    [yMax, monthsToShowFormatted]
  );

  // CUSTOM
  const allTreatmentPlansAreZero = data.every(
    (item) => item.totalTreatmentPlans === 0
  );

  const handleTooltipShow = useCallback(
    (event) => {
      // Prevent tooltip if all totalTreatmentPlans are zero
      if (allTreatmentPlansAreZero) {
        return;
      }

      const { x } = localPoint(event) || { x: 0 };
      const xPos = x - defaultMargin.left;

      // Find the index of the closest month by comparing xPos to the center of each band
      let closestIndex = 0;
      monthsToShowFormatted.forEach((d, i) => {
        const bandCenter = xScale(getMonth(d)) + xScale.bandwidth() / 2;
        if (
          Math.abs(xPos - bandCenter) <
          Math.abs(
            xPos -
              (xScale(getMonth(monthsToShowFormatted[closestIndex])) +
                xScale.bandwidth() / 2)
          )
        ) {
          closestIndex = i;
        }
      });

      const closestDataPoint = monthsToShowFormatted[closestIndex];

      if (closestDataPoint) {
        showTooltip({
          tooltipData: closestDataPoint,
          tooltipLeft:
            xScale(getMonth(closestDataPoint)) + xScale.bandwidth() / 2,
          tooltipTop: yScale(getTotalPrice(closestDataPoint)),
        });
      }
    },
    [
      xScale,
      yScale,
      showTooltip,
      monthsToShowFormatted,
      allTreatmentPlansAreZero,
    ]
  );

  const handleMouseLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  if (width < 10) return null;

  // Generate path strings for total and completed price lines
  const totalPricePath = getLinePath(
    monthsToShowFormatted,
    xScale,
    yScale,
    getMonth,
    getTotalPrice
  );
  const completedPricePath = getLinePath(
    monthsToShowFormatted,
    xScale,
    yScale,
    getMonth,
    getCompletedPrice
  );

  const MotionPath = motion.path;

  // Define a formatter function to convert large numbers into readable format
  const numberFormatter = (value) => {
    if (value >= 1000000) {
      return `${value / 1000000}m`; // Format values in millions
    } else if (value >= 1000) {
      return `${value / 1000}k`; // Format values in thousands
    }
    return value; // Return the original value if it's below 1000
  };

  // LOGS

  // console.log("data:", data);

  return (
    <>
      <Flex w="100%" position="relative" ref={chartRef}>
        <svg width={width} height={height}>
          <Group top={defaultMargin.top} left={defaultMargin.left}>
            <GridRows
              scale={yScale}
              width={xMax}
              stroke={backgroundGreyMode}
              strokeDasharray="2,2"
            />

            {/* X Axis */}
            <motion.g
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <AxisBottom
                top={yMax + 0}
                scale={xScale}
                stroke={backgroundGrey}
                // stroke="transparent"
                // tickStroke={white}
                tickStroke="transparent"
                tickLabelProps={() => ({
                  fill: textSecondary,
                  fontSize: "12px",
                  dx: "-8px",
                  dy: "-4.5px",
                  // textAlign: "center",
                })}
              />
            </motion.g>

            {!allTreatmentPlansAreZero && (
              <>
                {/* Animated Line for total services price */}
                <MotionPath
                  d={totalPricePath} // path generated by getLinePath function
                  fill="none"
                  stroke={primary}
                  strokeWidth={2}
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.6 }}
                />

                {/*Animated line for completed services price */}
                <MotionPath
                  d={completedPricePath} // path generated by getLinePath function
                  fill="none"
                  stroke={secondary}
                  strokeWidth={2}
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.8 }}
                />

                {monthsToShowFormatted.map((d, i) => {
                  const xPosition = xScale(getMonth(d)); // Use the exact position from xScale
                  const yPositionTotal = yScale(getTotalPrice(d));
                  const yPositionCompleted = yScale(getCompletedPrice(d));

                  return (
                    <g key={`circle-${i}`}>
                      {/* Circle for total price */}
                      <circle
                        cx={xPosition}
                        cy={yPositionTotal}
                        r={3}
                        fill={primary}
                      />

                      {/* Circle for completed services */}
                      <circle
                        cx={xPosition}
                        cy={yPositionCompleted}
                        r={3}
                        fill={secondary}
                      />
                    </g>
                  );
                })}
              </>
            )}

            {/* Y Axis */}
            <AxisLeft
              scale={yScale}
              stroke={backgroundGrey}
              tickStroke={backgroundGrey}
              hideTicks
              tickLabelProps={() => ({
                fill: textSecondary,
                fontSize: "12px",
                dx: "-30px",
                dy: "4px",
              })}
              numTicks={6}
              // left={defaultMargin.left}
              // top={defaultMargin.top}
              orientation="left"
              //   hideAxisLine
              //   hideZero
              tickFormat={numberFormatter}
            />

            {/* Overlay for tooltip interaction */}
            <rect
              width={xMax}
              height={yMax}
              fill="transparent"
              onMouseMove={handleTooltipShow}
              onMouseLeave={handleMouseLeave}
            />
          </Group>
        </svg>

        {tooltipOpen && tooltipData && (
          //   <TooltipWithBounds left={tooltipLeft} top={tooltipTop}>
          //     <div>
          //       <strong>{tooltipData.month}</strong>
          //       <br />
          //       Total Price: ${getTotalPrice(tooltipData)}
          //       <br />
          //       Completed Price: ${getCompletedPrice(tooltipData)}
          //     </div>
          //   </TooltipWithBounds>
          <TooltipWithBounds
            left={tooltipLeft}
            top={tooltipTop}
            style={{
              ...defaultStyles,
              boxShadow: "0px 2px 5px rgba(27, 47, 55, 0.2)",
              padding: "0.5rem 1rem",
              borderRadius: "1rem",
              backgroundColor: "#2D3748",
              color: "white",
              fontSize: "0.8rem",
              fontWeight: "500",
              whiteSpace: "nowrap",
            }}
          >
            <Flex
              align="flex-start"
              justify="center"
              direction="column"
              minW="180px"
              gap={1}
            >
              <Text
                fontSize="0.8rem"
                fontWeight="500"
                mr={1}
                mb={2}
                textTransform="capitalize"
              >
                {getMonthLabelFullWithYear(
                  tooltipData.monthNumber,
                  tooltipData.year
                )}
              </Text>

              <Flex align="center" justify="space-between" w="100%">
                <Text fontSize="xs" fontWeight="500" mr={1}>
                  {t("dashboard.totalServicesAmount")}:
                </Text>
                <Text fontSize="xs" fontWeight="600" mr={1}>
                  {currencySymbol} {formatNumber(getTotalPrice(tooltipData))}
                  {/* {getTotalPrice(tooltipData)} */}
                </Text>
              </Flex>

              <Flex align="center" justify="space-between" w="100%">
                <Text fontSize="xs" fontWeight="500">
                  {t("dashboard.totalCompletedServicesAmount")}:
                </Text>
                <Text fontSize="xs" fontWeight="600" ml={1}>
                  {currencySymbol}{" "}
                  {formatNumber(getCompletedPrice(tooltipData))}
                  {/* Format here */}
                  {/* {getCompletedPrice(tooltipData)} */}
                </Text>
              </Flex>
            </Flex>
          </TooltipWithBounds>
        )}
      </Flex>
    </>
  );
};

export default ServicesLineChart;
